import { Button } from 'app/components/ui/button'
import { Badge } from 'app/components/ui/badge'
import { Input } from 'app/components/ui/input'
import { Label } from 'app/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'app/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'app/components/ui/table'
import type { TestContact, FakeContactsActionData } from './useFakeContacts'

export type FakeContactsShadcnComponentProps = {
  testPhoneNumbers: string[]
  locations: { id: string; name: string }[]
  testContacts: TestContact[]
  hasWorkspace: boolean
  actionData: FakeContactsActionData | undefined
  isCreating: boolean
  isDeleting: boolean
  onCreate: (fields: Record<string, string>) => void
  onDelete: (contactId: string) => void
}

export function FakeContactsShadcnComponent({
  testPhoneNumbers,
  locations,
  testContacts,
  hasWorkspace,
  actionData,
  isCreating,
  isDeleting,
  onCreate,
  onDelete,
}: FakeContactsShadcnComponentProps) {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-strong">Fake Contacts</h1>
        <p className="mt-2 text-sm text-subtle">Create test contacts for broken appointment workflow testing</p>
      </div>

      {!hasWorkspace && (
        <div className="rounded-md bg-yellow-50 p-3 text-sm text-yellow-800">
          Select a workspace to use this page. Interactions are disabled until a workspace is selected.
        </div>
      )}

      {/* Creation Form */}
      <div className="rounded-lg bg-surface p-6 shadow">
        <h2 className="mb-4 text-lg font-semibold">Create Test Contact</h2>

        {actionData && `error` in actionData && (
          <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-800">{actionData.error}</div>
        )}

        {actionData && `success` in actionData && `contactId` in actionData && (
          <div className="mb-4 rounded-md bg-green-50 p-3 text-sm text-green-800">
            Successfully created test contact (ID: {actionData.contactId})
          </div>
        )}

        {actionData && `deleted` in actionData && (
          <div className="mb-4 rounded-md bg-green-50 p-3 text-sm text-green-800">
            Deleted test contact (ID: {actionData.deleted})
          </div>
        )}

        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault()
            onCreate(Object.fromEntries([...new FormData(e.currentTarget)].map(([key, value]) => [key, String(value)])))
          }}
        >
          <div className="grid grid-cols-1 items-end gap-4 md:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="phone">Phone Number</Label>
              <Select name="phone" required disabled={isCreating || !hasWorkspace}>
                <SelectTrigger id="phone">
                  <SelectValue placeholder="Select test phone number" />
                </SelectTrigger>
                <SelectContent>
                  {testPhoneNumbers.map((phone) => (
                    <SelectItem key={phone} value={phone}>
                      {phone}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="contactName">Contact Name (optional)</Label>
              <Input
                id="contactName"
                name="contactName"
                placeholder="Test Contact [timestamp]"
                disabled={isCreating || !hasWorkspace}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="locationId">Location</Label>
              <Select name="locationId" required disabled={isCreating || !hasWorkspace}>
                <SelectTrigger id="locationId">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((location) => (
                    <SelectItem key={location.id} value={location.id}>
                      {location.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button
            type="submit"
            variant="primary"
            size="md"
            loading={isCreating}
            disabled={!hasWorkspace || testPhoneNumbers.length === 0 || locations.length === 0}
          >
            {isCreating ? `Creating...` : `Create Test Contact with Broken Appointment`}
          </Button>

          {testPhoneNumbers.length === 0 && (
            <p className="text-sm text-orange-600">
              No test phone numbers configured. Set TRACKABLE_EMPLOYEE_NUMBERS in environment.
            </p>
          )}
          {hasWorkspace && locations.length === 0 && (
            <p className="text-sm text-orange-600">No locations found in the current workspace.</p>
          )}
        </form>
      </div>

      {/* Test Contacts List */}
      <div className="rounded-lg border border-default bg-surface p-6 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Test Contacts</h2>
          <Button variant="white" size="sm" disabled={!hasWorkspace} onClick={() => window.location.reload()}>
            Refresh
          </Button>
        </div>

        {testContacts.length === 0 ? (
          <p className="text-subtle">No test contacts found</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Contact Name</TableHead>
                  <TableHead>Phone Number</TableHead>
                  <TableHead>Appt. Status</TableHead>
                  <TableHead>Tags</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {testContacts.map((contact) => (
                  <TableRow key={contact.id}>
                    <TableCell className="font-medium">
                      <a href={`/contacts?drawer=${contact.id}`} target="_blank" rel="noopener noreferrer">
                        {contact.name}
                      </a>
                    </TableCell>
                    <TableCell>{contact.cellPhone || contact.homePhone || contact.workPhone || `N/A`}</TableCell>
                    <TableCell>{contact.appointmentStatus}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {contact.tags?.map((tag) => (
                          <Badge key={tag} label={tag} color="gray" />
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>{new Date(contact.createdDate!).toLocaleString()}</TableCell>
                    <TableCell>
                      <Button
                        variant="danger"
                        size="sm"
                        disabled={isDeleting || !hasWorkspace}
                        onClick={() => onDelete(contact.id!)}
                      >
                        {isDeleting ? `Deleting...` : `Delete`}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  )
}
